import { Button } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { BikeleasingContractDetailsDTOStatusEnum } from 'probonio-shared-ui/api';
import { apis, downloadResponse } from 'probonio-shared-ui/module/api';
import { useMeStateSelector } from 'probonio-shared-ui/module/me/types';
import { useBikeleasingPortalLinkBuilder } from 'probonio-shared-ui/utils/bikeleasingUrl';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLinkButton } from '../../../../component/button/ExternalLinkButton';

const DownloadButton: React.FC<{ contractId: string }> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const tenantId = useMeStateSelector(state => state.me.currentTenantId);
  const [pdfAvailable, setPdfAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    setPdfAvailable(null);
  }, [contractId]);

  const { mutate: download, isPending } = useMutation({
    mutationFn: async (mutationContractId: string) => {
      if (!tenantId) throw new Error('Missing current tenant id');
      const response = await apis.bikeLeasingV2.downloadContractFile(
        { tenantId, contractId: mutationContractId },
        { responseType: 'blob' },
      );
      downloadResponse(response, 'contract.pdf');
    },
    onError: (err, mutationContractId) => {
      if (mutationContractId !== contractId) {
        return;
      } else if (err instanceof AxiosError && err.response?.status === 404) {
        setPdfAvailable(false);
      } else {
        enqueueSnackbar({ variant: 'error', message: t('bikeleasing_v2.contract.downloadError') });
      }
    },
  });

  const isUnavailable = pdfAvailable === false;

  return (
    <Button loading={isPending} onClick={() => download(contractId)} disabled={!tenantId || isUnavailable} sx={{ height: 'auto' }}>
      {isUnavailable ? t('bikeleasing_v2.contract.downloadNotAvailable') : t('bikeleasing_v2.contract.download')}
    </Button>
  );
};

const RequestEarlyBuyoutOfferButton: React.FC<{ contractId: string }> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();

  return (
    <ExternalLinkButton href={buildBikeleasingPortalLink(`/user/contracts/${contractId}?REQUEST_BUYOUT_OFFER=true`)}>
      {t('bikeleasing_v2.contract.requestEarlyBuyoutOffer')}
    </ExternalLinkButton>
  );
};

const ReportBikeDamageButton: React.FC<{ contractId: string }> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();

  return (
    <ExternalLinkButton href={buildBikeleasingPortalLink(`/user/report-damage/${contractId}`)}>
      {t('bikeleasing_v2.contract.reportBikeDamage')}
    </ExternalLinkButton>
  );
};
const CreateInsuranceClaimButton: React.FC<{ contractId: string }> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();

  return (
    <ExternalLinkButton href={buildBikeleasingPortalLink(`/user/employer/insurance/create/${contractId}`)}>
      {t('extensions:bikeleasing_v2.contract.claimInsuranceCase')}
    </ExternalLinkButton>
  );
};

const SignContractButton: React.FC<{ contractId: string }> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();

  return (
    <ExternalLinkButton href={buildBikeleasingPortalLink(`/user/contracts/${contractId}?EMPLOYER_SIGN_CONTRACT=true`)}>
      {t('extensions:bikeleasing_v2.contract.signContract')}
    </ExternalLinkButton>
  );
};

const ApproveRejectButton: React.FC<{ contractId: string }> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();

  return (
    <ExternalLinkButton href={buildBikeleasingPortalLink(`/user/contracts/${contractId}?EMPLOYER_SHOW_APPROVE_REJECT=true`)}>
      {t('extensions:bikeleasing_v2.contract.approveReject')}
    </ExternalLinkButton>
  );
};

export function useContractButtonsForStatus(
  contractId: string,
  status?: BikeleasingContractDetailsDTOStatusEnum,
  isCompanyOrDealerBike?: boolean,
) {
  switch (status) {
    case BikeleasingContractDetailsDTOStatusEnum.Active:
    case BikeleasingContractDetailsDTOStatusEnum.Expiring:
      return (
        <>
          <CreateInsuranceClaimButton contractId={contractId} />
          {isCompanyOrDealerBike && <ReportBikeDamageButton contractId={contractId} />}
          <RequestEarlyBuyoutOfferButton contractId={contractId} />
          <DownloadButton contractId={contractId} />
        </>
      );
    case BikeleasingContractDetailsDTOStatusEnum.EmployerApprovalRequired:
      return (
        <>
          <ApproveRejectButton contractId={contractId} />
          <SignContractButton contractId={contractId} />
          <DownloadButton contractId={contractId} />
        </>
      );
    case BikeleasingContractDetailsDTOStatusEnum.Requested:
    case BikeleasingContractDetailsDTOStatusEnum.EmployeeApprovalRequired:
    case BikeleasingContractDetailsDTOStatusEnum.BikeTransferRequired:
    case BikeleasingContractDetailsDTOStatusEnum.BikeShipped:
    case BikeleasingContractDetailsDTOStatusEnum.BikeTransferDone:
    case BikeleasingContractDetailsDTOStatusEnum.CancelledOrRejected:
    case BikeleasingContractDetailsDTOStatusEnum.Finished:
    default:
      return <DownloadButton contractId={contractId} />;
  }
}
